import { useCallback, useEffect, useState } from 'react';

import { api, type AutoLista, type AutoSor, type Sofor } from '../api.ts';
import { szam } from '../formazas.ts';
import { Mezo, SzamMezo, ValasztoMezo } from './Mezo.tsx';

/**
 * Sofőrök és autók. (Megbízás 6.9)
 *
 * A név átírása visszamenőleg minden rögzített napon érvényes — a napi tételek
 * a sofőr azonosítójára hivatkoznak, nem a nevére, ezért nincs mit átvezetni.
 *
 * Aki már szerepel rögzített napon, azt nem lehet törölni, csak inaktiválni:
 * a törlés meghamisítaná a múltat.
 */
export function Soforok({ fejlec }: { fejlec?: React.ReactNode }) {
  const [sorok, setSorok] = useState<Sofor[]>([]);
  /** Az autók a legördülőhöz — a rendszám innentől nem szabad szöveg. */
  const [autok, setAutok] = useState<AutoSor[]>([]);
  const [hiba, setHiba] = useState<string | null>(null);
  const [toltes, setToltes] = useState(true);
  const [ujNev, setUjNev] = useState('');

  const betolt = useCallback(async () => {
    setToltes(true);
    try {
      const [eredmeny, autoLista] = await Promise.all([
        api.get<{ sorok: Sofor[] }>('/api/sofor'),
        api.get<AutoLista>('/api/auto'),
      ]);
      setSorok(eredmeny.sorok);
      setAutok(autoLista.sorok);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni.');
    } finally {
      setToltes(false);
    }
  }, []);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const ment = (id: number, mezo: string) => async (ertek: string | boolean) => {
    const frissitett = await api.patch<Sofor>(`/api/sofor/${id}/mezo/${mezo}`, { ertek });
    setSorok((elozo) => elozo.map((s) => (s.id === id ? frissitett : s)));
  };

  async function ujFelvetel() {
    const nev = ujNev.trim();
    if (!nev) return;
    try {
      await api.post<Sofor>('/api/sofor', { nev });
      setUjNev('');
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A felvétel nem sikerült.');
    }
  }

  async function torol(s: Sofor) {
    if (!confirm(`Biztosan törlöd? — ${s.nev}`)) return;
    try {
      await api.torol(`/api/sofor/${s.id}`);
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A törlés nem sikerült.');
    }
  }

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Sofőrök és autók</span>
        {fejlec}
        <div className="stats">
          <div>
            Sofőr
            <strong>{sorok.length}</strong>
          </div>
          <div>
            Aktív
            <strong>{sorok.filter((s) => s.aktiv).length}</strong>
          </div>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)} title="Kattints az elrejtéshez">
          {hiba}
        </div>
      )}

      <div className="actbar" style={{ marginTop: 16 }}>
        <input
          className="qi"
          style={{ width: 220, fontSize: 12.5 }}
          placeholder="új sofőr neve…"
          value={ujNev}
          onChange={(e) => setUjNev(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void ujFelvetel()}
        />
        <button className="btn sm" disabled={!ujNev.trim()} onClick={() => void ujFelvetel()}>
          + Új sofőr / autó
        </button>
        <span className="hint">
          A név módosítása a már rögzített napokon is átvezetődik. A 2.0-ban ez
          felhasználói fiókhoz kötődik majd.
        </span>
      </div>

      <div className="matrix">
        <table className="tt2">
          <thead>
            <tr>
              <th>Sofőr neve</th>
              <th>Autó</th>
              <th className="n">Induló készlet (l)</th>
              <th className="n">Jelenlegi készlet</th>
              <th>Állapot</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((s) => (
              <tr key={s.id} className={s.aktiv ? undefined : 'nul'}>
                <td>
                  <Mezo ertek={s.nev} onMent={ment(s.id, 'nev')} />
                </td>
                <td>
                  <ValasztoMezo
                    ertek={s.auto_id === null ? null : String(s.auto_id)}
                    lehetosegek={autok.map((a) => ({ ertek: String(a.id), cimke: a.rendszam }))}
                    uresCimke="— nincs autó —"
                    onMent={ment(s.id, 'auto_id')}
                  />
                </td>
                <td>
                  <SzamMezo ertek={s.nyito_keszlet} onMent={ment(s.id, 'nyito_keszlet')} />
                </td>
                <td className="calc2" title="Nyitó + felrakodás − átadás − kiszállítva">
                  {szam(s.jelenlegi_keszlet, 1)}
                </td>
                <td>
                  <label className="sw" style={{ padding: '0 9px' }}>
                    <input
                      type="checkbox"
                      checked={s.aktiv}
                      onChange={(e) => void ment(s.id, 'aktiv')(e.target.checked)}
                    />
                    aktív
                  </label>
                </td>
                <td className="act">
                  <button
                    className="xb"
                    title={
                      s.torolheto
                        ? 'Törlés'
                        : `Nem törölhető: ${s.jarat_sorok} járatsoron és ${s.elszamolasok} elszámoláson szerepel. Vedd ki az "aktív" jelölést helyette.`
                    }
                    disabled={!s.torolheto}
                    onClick={() => void torol(s)}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {toltes
                    ? 'Betöltés…'
                    : 'Még nincs sofőr felvéve. Írd be a nevét a fenti mezőbe.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
